/**
 * 고스트(SR-LCL-03 · SCR v0.6 개정 3) — 친구 리플레이 ≤3, 이름·완주 여부만(시간·순위·비교 없음).
 * S4: 후보는 ① 제어 평면 리플레이(GET …/replays?modeId&presetKey — 친구 것은 별명, 내 것은 '나의 지난 주행 n')
 *     ② 아직 못 올린 내 주행(오프라인 보관함 — {@link ./replaySave}). S3 의 '이 기기에만 남기는 위치 트랙 고스트'는
 *     오프라인 보관함으로 바뀌었다: 올리지 못한 주행만 이 기기에 남고, 연결되면 올린 뒤 지운다.
 * 불러온 고스트는 커널에 리플레이 그대로(ghostReplays — 입력 재생, 어긋나면 위치 트랙) 넘긴다.
 */
import type { GhostReplay, GhostTrack, RunBundle } from "@picnic-field/local-host/protocol";
import type { GhostLoad, GhostSource, GhostContext } from "../types";

/** 표시할 고스트 이름·완주 여부 */
export const ghostOf = (g: GhostLoad): { ghostId: string; label: string; bundle: RunBundle } =>
  g.kind === "replay" ? g.replay : g.track;

/** 제어 평면 리플레이 → 후보(최근 순 · 목록 응답에 시간 없음) */
export function serverGhostSource(ctx: GhostContext): GhostSource {
  return {
    async list(presetKey) {
      const items = await ctx.social.replays({ modeId: ctx.modeId, presetKey, limit: 12 });
      let mineN = 0;
      return items.map((r) => {
        const label = r.mine ? ctx.mineLabel(++mineN) : r.nickname;
        return {
          ghostId: r.replayId,
          label,
          completed: r.completed,
          source: r.mine ? ("mine" as const) : ("friend" as const),
          load: async (): Promise<GhostLoad> => {
            const [info, file, track] = await Promise.all([
              ctx.social.replay(r.replayId),
              ctx.social.replayFile(r.replayId),
              ctx.social.replayTrack(r.replayId),
            ]);
            // 스냅샷이 여럿이면 첫 번째(기록한 학생의 기계)
            const bundle = await ctx.runBundle(info.snapshotIds[0] ?? r.snapshotId);
            return {
              kind: "replay",
              replay: {
                ghostId: r.replayId,
                label,
                completed: r.completed,
                bundle,
                file,
                positionTrack: track,
              },
            };
          },
        };
      });
    },
  };
}

export function combineSources(...sources: GhostSource[]): GhostSource {
  return {
    async list(presetKey) {
      const lists = await Promise.all(sources.map((s) => s.list(presetKey).catch(() => [])));
      const seen = new Set<string>();
      return lists.flat().filter((g) => !seen.has(g.ghostId) && !!seen.add(g.ghostId));
    },
  };
}

/** 선택 토글 — 최대 max 개(SR-LCL-03 ≤3). 넘치면 그대로 */
export function toggleGhost(selected: readonly string[], id: string, max: number): string[] {
  if (selected.includes(id)) return selected.filter((x) => x !== id);
  if (selected.length >= max) return [...selected];
  return [...selected, id];
}

/** LoadRequest 로 나누기 — 위치 트랙 고스트(ghosts)와 리플레이 고스트(ghostReplays), 합쳐 max 개 */
export function splitGhosts(
  loads: readonly GhostLoad[],
  max: number,
): { ghosts: GhostTrack[]; ghostReplays: GhostReplay[] } {
  const ghosts: GhostTrack[] = [];
  const ghostReplays: GhostReplay[] = [];
  for (const g of loads.slice(0, max)) {
    if (g.kind === "replay") ghostReplays.push(g.replay);
    else ghosts.push(g.track);
  }
  return { ghosts, ghostReplays };
}
